"use client";

import { IconCopy, IconKey, IconLoader, IconRefresh } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useStoreIntegrationKey } from "@/features/store-settings/hooks/use-store-integration-key";
import { useStoreSlug } from "@/hooks/use-store-slug";
import { useStores } from "@/hooks/use-stores";

export const Route = createFileRoute("/$store/_layout/settings_/api")({
	component: StoreApiSettingsPage,
});

function formatDateTime(value: string | null): string {
	if (!value) return "—";
	const date = new Date(value);
	return date.toLocaleString("pt-BR");
}

function maskKeyId(keyId: string | null): string {
	if (!keyId) return "—";
	if (keyId.length <= 8) return keyId;
	return `${keyId.slice(0, 4)}...${keyId.slice(-4)}`;
}

function DocsBlock({ apiKey }: { apiKey: string | null }) {
	const apiUrl = import.meta.env.VITE_API_URL || "https://api.oatomicstore.com";
	const key = apiKey || "SUA_API_KEY_AQUI";

	const uploadExample = `curl -X POST "${apiUrl}/catalog/integration/product-images/upload" \\
  -H "x-api-key: ${key}" \\
  -F "file=@/caminho/imagem1.png" \\
  -F "file=@/caminho/imagem2.jpg"`;

	const createExample = `curl -X POST "${apiUrl}/catalog/integration/products/create" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${key}" \\
  -d '{
    "title": "Produto importado",
    "slug": "produto-importado",
    "status": "active",
    "variants": [
      {
        "sku": "SKU-001",
        "priceBrl": 99.9,
        "title": "Padrão",
        "barcode": "7890000000001",
        "isDefault": true
      }
    ],
    "images": [
      { "url": "https://cdn.exemplo.com/imagem.webp", "sortOrder": 0 }
    ]
  }'`;

	return (
		<Card>
			<CardHeader>
				<CardTitle>Como usar</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				<p className="text-sm text-muted-foreground">
					1. Envie imagens no endpoint de upload. 2. Use as URLs retornadas para criar
					o produto no endpoint de integração.
				</p>
				<div className="space-y-2">
					<p className="text-sm font-medium">Upload de imagens</p>
					<pre className="rounded-md border bg-muted p-3 text-xs overflow-x-auto whitespace-pre-wrap">
						{uploadExample}
					</pre>
				</div>
				<div className="space-y-2">
					<p className="text-sm font-medium">Criar produto</p>
					<pre className="rounded-md border bg-muted p-3 text-xs overflow-x-auto whitespace-pre-wrap">
						{createExample}
					</pre>
				</div>
			</CardContent>
		</Card>
	);
}

function StoreApiSettingsPage() {
	const storeSlug = useStoreSlug();
	const { stores, isLoading: isLoadingStores } = useStores();
	const currentStore = React.useMemo(() => {
		if (!storeSlug) return null;
		return stores.find((store) => store.slug === storeSlug) || null;
	}, [storeSlug, stores]);

	const { status, isLoading, regenerateIntegrationKey, isRegenerating } =
		useStoreIntegrationKey(currentStore?.id);
	const [generatedApiKey, setGeneratedApiKey] = React.useState<string | null>(null);

	async function handleRegenerate() {
		const response = await regenerateIntegrationKey();
		setGeneratedApiKey(response.apiKey);
	}

	async function handleCopy(value: string) {
		await navigator.clipboard.writeText(value);
		toast.success("Copiado para a área de transferência.");
	}

	if (isLoadingStores || isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<IconLoader className="size-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (!currentStore) {
		return (
			<div className="flex items-center justify-center h-64">
				<p className="text-muted-foreground">Loja não encontrada.</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-6 px-4 py-6 lg:px-6">
			<div>
				<h1 className="text-2xl font-semibold text-foreground">API da Loja</h1>
				<p className="text-sm text-muted-foreground">
					Gerencie a API key de integração para bots/importadores sem login.
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<IconKey className="size-5" />
						Status da API key
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex flex-wrap items-center gap-3">
						<Badge variant={status?.enabled ? "default" : "outline"}>
							{status?.enabled ? "Ativa" : "Não configurada"}
						</Badge>
						<span className="text-sm text-muted-foreground">
							ID: {maskKeyId(status?.keyId ?? null)}
						</span>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
						<div>
							<p className="text-muted-foreground">Criada em</p>
							<p>{formatDateTime(status?.createdAt ?? null)}</p>
						</div>
						<div>
							<p className="text-muted-foreground">Último uso</p>
							<p>{formatDateTime(status?.lastUsedAt ?? null)}</p>
						</div>
					</div>
					<Button onClick={handleRegenerate} disabled={isRegenerating}>
						{isRegenerating ? (
							<IconLoader className="mr-2 size-4 animate-spin" />
						) : (
							<IconRefresh className="mr-2 size-4" />
						)}
						Regenerar API key
					</Button>
				</CardContent>
			</Card>

			{generatedApiKey ? (
				<Card>
					<CardHeader>
						<CardTitle>Nova API key</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2">
						<p className="text-sm text-muted-foreground">
							Essa chave é exibida apenas agora. Salve em local seguro.
						</p>
						<div className="rounded-md border bg-muted p-3 text-xs break-all">
							{generatedApiKey}
						</div>
						<Button
							variant="outline"
							type="button"
							onClick={() => handleCopy(generatedApiKey)}
						>
							<IconCopy className="mr-2 size-4" />
							Copiar API key
						</Button>
					</CardContent>
				</Card>
			) : null}

			<DocsBlock apiKey={generatedApiKey} />
		</div>
	);
}
